import { Boxes, Languages, LogOut, Moon, RefreshCw, Sun } from 'lucide-react';
import type { ReactNode } from 'react';
import { NavLink } from 'react-router';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select } from '@/components/ui/select';
import { useAuth } from '@/lib/auth';
import { useIslands } from '@/lib/islands';
import { useI18n } from '@/lib/i18n';
import { navSekcjeFor } from '@/lib/nav';
import { useTheme } from '@/lib/theme';
import { fetchBranding, logoUrl } from '@/lib/branding-api';
import { useQuery } from '@tanstack/react-query';
import { Outlet } from 'react-router';
import { cn } from '@/lib/utils';

/**
 * ⚠️⚠️ `children` NIEOBOWIĄZKOWE, BO POWŁOKA JEST TERAZ TAKŻE TRASĄ UKŁADU. Od 15.09.2026 pulpit
 * maszyny otwiera się w OSOBNYM OKNIE i musi renderować się BEZ menu i nagłówka — a to wymaga
 * trasy stojącej poza powłoką. Najprościej i najzgodniej z routerem: powłoka jest trasą układu,
 * a ekrany wchodzą w nią przez `Outlet`.
 *
 * ⚠️ ZOSTAWIAMY DROGĘ PRZEZ `children`, żeby nie przepisywać wszystkich miejsc, które renderują
 * powłokę wprost — a przy okazji to ona jest czytelniejsza w testach.
 */
export function AppShell({ children, version }: { children?: ReactNode; version?: string }) {
  const { theme, toggle } = useTheme();
  const { t, lang, setLang } = useI18n();
  const { user, logout } = useAuth();
  /**
   * Lista pozycji liczona przez `navFor` z `lib/nav.ts` — TĄ SAMĄ funkcją, którą `App.tsx`
   * bramkuje trasy. Wcześniej tabela stała tutaj, a trasy nie znały jej wcale: menu ukrywało
   * pozycję, ale wpisanie adresu z palca otwierało ekran.
   */
  const sekcje = navSekcjeFor(user);
  // branding jest publiczny i rzadko się zmienia — trzymamy go długo w pamięci zapytań
  const { data: branding } = useQuery({ queryKey: ['branding'], queryFn: fetchBranding, staleTime: 300_000 });
  const { islands, groupId, setGroupId, island } = useIslands();

  /**
   * SYNCHRONIZACJA W TOKU — jedna liczba i jedno zdanie do podpowiedzi.
   *
   * ⚠️ SUMUJEMY DWA STANY, BO PYTANIE JEST JEDNO: „czy coś się teraz przelewa". Rozróżnienie
   * (dociąganie historii po przewinięciu / zwykła zaległość kanału) siedzi w podpowiedzi, bo
   * pierwsze jest normą trwającą godzinami, a drugie — rzeczą do sprawdzenia.
   */
  const sync = (() => {
    const s = island?.synchronizacja;
    if (s == null) return null;
    const wezlow = s.dociaganie + s.zaleglosc;
    if (wezlow === 0) return null;
    const czesci = [
      s.dociaganie > 0 ? t('islands.sync.dociaganie').replace('{n}', String(s.dociaganie)) : null,
      s.zaleglosc > 0 ? t('islands.sync.zaleglosc').replace('{n}', String(s.zaleglosc)) : null,
    ].filter((x): x is string => x !== null);
    return { wezlow, opis: czesci.join(' ') };
  })();

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <aside className="hidden w-60 shrink-0 flex-col border-r border-sidebar-border bg-sidebar md:flex">
        <div className="flex h-14 items-center gap-2 border-b border-sidebar-border px-5">
          {/*
            BRANDING INSTANCJI (PLAN §8a). Gdy klient nie wgrał logo, zostaje nasza ikona —
            zamiast puste miejsce albo obrazek zastępczy udający logo. Adres obrazu niesie
            WERSJĘ, bo odpowiedź z logo jest buforowana na rok; bez wersji podmiana byłaby
            niewidoczna do wyczyszczenia pamięci przeglądarki.
          */}
          {branding?.hasLogo ? (
            /*
              ⚠️⚠️ LOGO **I** NAZWA, nie logo ZAMIAST nazwy (zgłoszone 02.09.2026: „przy wgranym
              logo nie wyświetla się w ogóle nazwa instancji w lewym górnym rogu"). Poprzednia
              wersja podmieniała jedno na drugie, więc po wgraniu logo z ekranu znikała jedyna
              odpowiedź na „na której instancji jestem" — a przy kilkudziesięciu instancjach
              o tym samym układzie to jest pytanie zadawane najczęściej.

              ⚠️ LOGO WĘŻSZE NIŻ WCZEŚNIEJ (`max-w-24`), bo teraz dzieli szerokość z nazwą; nazwa
              ucina się z `title`, zamiast rozpychać pasek.

              ⚠️ `alt` PUSTY: nazwa stoi obok jako tekst, więc czytnik ekranu nie ma jej czytać
              dwa razy — obrazek jest tu ozdobą, nie treścią.
            */
            <>
              <img
                src={logoUrl(branding.logoVersion)}
                alt=""
                className="max-h-8 max-w-24 shrink-0 object-contain"
              />
              <span className="truncate font-semibold tracking-tight" title={branding.displayName}>
                {branding.displayName}
              </span>
            </>
          ) : (
            <>
              <Boxes className="size-5 text-primary" />
              <span className="font-semibold tracking-tight">{branding?.displayName ?? t('app.name')}</span>
            </>
          )}
        </div>

        {/* Menu w sekcjach jak w starym Vendi: Pulpit i Automaty bez nagłówka, potem LUDZIE,
            ASORTYMENT i ADMINISTRACJA. Przy dwudziestu pozycjach płaska lista wymagała czytania
            wszystkich, żeby znaleźć jedną. Sekcja bez ani jednej widocznej pozycji NIE wypisuje
            nagłówka — inaczej menu obiecywałoby ekrany, do których to konto nie ma wstępu. */}
        <nav className="flex-1 overflow-y-auto p-3">
          {sekcje.map((sekcja, i) => (
            <div key={sekcja.id} className={i > 0 ? 'mt-4' : undefined}>
              {sekcja.key && (
                <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                  {t(sekcja.key)}
                </p>
              )}
              <div className="space-y-0.5">
                {sekcja.items.map(({ to, key, icon: Icon, ready }) =>
                  ready ? (
                    <NavLink
                      key={to}
                      to={to}
                      end
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors',
                          isActive
                            ? 'bg-primary/10 font-medium text-primary'
                            : 'text-sidebar-foreground/80 hover:bg-accent hover:text-accent-foreground',
                        )
                      }
                    >
                      <Icon className="size-4" />
                      {t(key)}
                    </NavLink>
                  ) : (
                    <span
                      key={to}
                      aria-disabled="true"
                      title="Faza 4"
                      className="flex cursor-not-allowed items-center gap-2.5 rounded-md px-3 py-2 text-sm text-muted-foreground/50"
                    >
                      <Icon className="size-4" />
                      {t(key)}
                    </span>
                  ),
                )}
              </div>
            </div>
          ))}
        </nav>

        {version && (
          <div className="border-t border-sidebar-border px-5 py-3 text-xs text-muted-foreground">
            v{version}
          </div>
        )}
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 items-center gap-1 border-b border-border bg-card/50 px-5">
          {/*
            PRZEŁĄCZNIK WYSPY TYLKO GDY JEST CO PRZEŁĄCZAĆ.
            Decyzja z 2026-07-31: docelowo JEDNA baza master SPS na instancję (§8a), więc
            na produkcji jest dokładnie jedna wyspa. Lista rozwijana z jedną pozycją udaje
            wybór, którego nie ma, i każe użytkownikowi sprawdzać, czy czegoś nie przeoczył.
            Nazwa zostaje jako TEKST, bo „w której bazie jestem" to informacja potrzebna
            zawsze — zniknąć ma tylko fałszywy wybór.
            Wiele wysp zostaje w kodzie dla devu i testów izolacji.
          */}
          {islands.length > 1 ? (
            <Select
              value={groupId ?? ''}
              onChange={(e) => setGroupId(e.target.value)}
              aria-label={t('islands.switch')}
              title={t('islands.switch')}
              /*
                ⚠️ SZEROKOŚĆ Z NAJDŁUŻSZEJ POZYCJI, NIE STAŁA. Przy nazwach w rodzaju
                „vendi-tyco-ergoflow-app — Tyco Krzywca IC" stałe 16 rem ucinało napis w połowie
                („Tyco Krzyw"), czyli przełącznik przestawał odpowiadać na pytanie, po co tam jest:
                w której wyspie jestem. `w-auto` na `select` daje szerokość najdłuższej opcji,
                a `max-w` trzyma to w ryzach na wąskim oknie. Miejsca w pasku jest w nadmiarze —
                dalej stoi rozpychacz `flex-1`.
              */
              className="h-8 w-auto min-w-64 max-w-[34rem] text-xs"
            >
              {islands.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.clientName} — {i.name}
                </option>
              ))}
            </Select>
          ) : islands.length === 1 ? (
            <span className="truncate text-xs text-muted-foreground" title={t('islands.single')}>
              {islands[0]!.clientName} — {islands[0]!.name}
            </span>
          ) : null}
          {/*
            PLAKIETKA TRYBU SMART SYNC — obok przełącznika, czyli w jedynym miejscu widocznym
            na KAŻDYM ekranie.
            ⚠️ Użytkownik poprosił o to wprost i z powodem: w trybie Smart Sync master stoi u nas
            w chmurze, a w zwykłym na maszynie — więc to samo kliknięcie znaczy w tych dwóch
            modelach co innego. Tryb zwykły plakietki NIE MA, bo jest stanem normalnym całej floty,
            a plakietka na wszystkim przestaje cokolwiek znaczyć.
          */}
          {island?.tryb === 'SMART_SYNC' && (
            <Badge variant="default" className="ml-2 shrink-0" title={t('islands.smartSyncHint')}>
              {t('islands.smartSync')}
            </Badge>
          )}
          {/*
            ⚠️⚠️ SYNCHRONIZACJA W TOKU — PRZY PLAKIETCE TRYBU, nie na ekranie kreatora. Zamówione
            02.09.2026 („czy możemy gdzieś u góry przy «SMART SYNC» dodać ikonkę, że synchronizacja
            jest w trakcie?") i przypomniane 11.09.2026: wskaźnik stanął wtedy na ekranie Smart
            Synca, czyli w jedynym miejscu, na które nikt nie wchodzi po wdrożeniu.

            ⚠️ ŻÓŁTA, NIE CZERWONA: dociąganie historii po przewinięciu trwa GODZINAMI i jest
            stanem normalnym — nowe wydania jadą przez ten czas zwyczajnie. Czerwień kazałaby
            szukać awarii tam, gdzie jej nie ma. Co dokładnie się dzieje, mówi podpowiedź.
          */}
          {sync !== null && (
            <Badge variant="warning" className="ml-1 shrink-0 gap-1" title={sync.opis}>
              <RefreshCw className="size-3 animate-spin" />
              {sync.wezlow}
            </Badge>
          )}
          <div className="flex-1" />
          {user && (
            <div className="mr-2 flex min-w-0 flex-col items-end leading-tight">
              <span className="truncate text-sm font-medium">{user.name ?? user.email}</span>
              <span className="text-xs text-muted-foreground">{t(`role.${user.role}`)}</span>
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setLang(lang === 'pl' ? 'en' : 'pl')}
            aria-label={`Język: ${lang.toUpperCase()}`}
            title={`Język: ${lang.toUpperCase()}`}
          >
            <Languages />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggle}
            aria-label={theme === 'dark' ? t('theme.toLight') : t('theme.toDark')}
            title={theme === 'dark' ? t('theme.toLight') : t('theme.toDark')}
          >
            {theme === 'dark' ? <Sun /> : <Moon />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => void logout()}
            aria-label={t('user.logout')}
            title={t('user.logout')}
          >
            <LogOut />
          </Button>
        </header>

        {/* ⚠️ `children` przy renderowaniu wprost, `Outlet` gdy powłoka jest trasą układu. */}
        <main className="flex-1 overflow-y-auto p-6">{children ?? <Outlet />}</main>
      </div>
    </div>
  );
}
